"use client";
import { Button } from "@/components/ui/button";
import { IBlocks, IUserData } from "@/types";
import React from "react";
import Link from "next/link";
import AdminPopover from "./utils/AdminPopover";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import RenderIconOrAsset from "./utils/RenderIconOrAsset";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import Blacklist from "@/lib/Blacklist";
import BlockClickHandler from "./utils/click";
import {
    BORDER_RADIUS_OPTIONS,
    FONT_SIZE_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";

interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}
export default function BlockLink({
    edit,
    account,
    block,
    update,
    style,
    theme,
}: Props) {
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const contentWarning = Blacklist?.find((e) =>
        blockUpdate.url?.includes(e.find)
    );
    const dataAttributes = Object.fromEntries(
        Object.entries(blockUpdate.metadata || {})
            .filter(([key]) => key.startsWith("html-data-"))
            .map(([key, value]) => [
                `data-metadata-${key.replace("html-data-", "")}`,
                value,
            ])
    );
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Link | " + block.url}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            {blockUpdate.adult || contentWarning ? (
                <Dialog>
                    <DialogTrigger asChild>
                        <div className={`w-full`}>
                            <button
                                style={style}
                                id="block"
                                className={cn(
                                    "rounded-user bg-user-primary-color text-user-primary-text-color",
                                    SHADOW_OPTIONS[theme.profile.design.shadow]
                                        ?.class,
                                    blockUpdate.style?.custom &&
                                        BORDER_RADIUS_OPTIONS[
                                            blockUpdate.style.corner_radius
                                        ]?.class,
                                    blockUpdate.style?.custom &&
                                        SHADOW_OPTIONS[blockUpdate.style.shadow]
                                            ?.class,
                                    blockUpdate.style?.custom &&
                                        FONT_SIZE_OPTIONS[
                                            blockUpdate.style.font_size
                                        ]?.class,
                                    "w-full px-1 py-3 text-xl transition-all duration-150 hover:scale-105",
                                    block.name.replaceAll(" ", "-"),
                                    "block-" + block.id
                                )}
                                {...dataAttributes}
                            >
                                <RenderIconOrAsset
                                    account_id={account.id}
                                    iconName={blockUpdate.icon}
                                    size={
                                        (blockUpdate.style?.custom &&
                                            FONT_SIZE_OPTIONS[
                                                blockUpdate.style.font_size
                                            ]?.icon) ||
                                        FONT_SIZE_OPTIONS[
                                            theme.profile.design.font_size
                                        ]?.icon ||
                                        26
                                    }
                                />
                                <span
                                    id="block-text"
                                    className={`block w-full px-6 text-center font-bold`}
                                >
                                    {blockUpdate.name}
                                </span>
                            </button>
                        </div>
                    </DialogTrigger>
                    <DialogContent className="sm:h-fit">
                        <DialogHeader>
                            <DialogTitle>
                                NSFW or mature content ahead
                            </DialogTitle>
                            <DialogDescription>
                                Where you're about to go has been flagged as
                                mature or NSFW
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col gap-4">
                            <p className="w-full rounded-md bg-accent p-1 text-center text-sm text-foreground/80">
                                {blockUpdate.url.replace("https://", "")}
                            </p>
                            <Separator />
                            <Label variant={"toggle"}>
                                Would you like to continue?
                            </Label>
                            <div className="flex w-full items-center gap-2">
                                <Link
                                    href={blockUpdate.url}
                                    target="_blank"
                                    prefetch={false}
                                    onClick={(e) => {
                                        BlockClickHandler(
                                            account,
                                            e,
                                            block,
                                            edit
                                        );
                                    }}
                                    onAuxClick={(e) => {
                                        BlockClickHandler(
                                            account,
                                            e,
                                            block,
                                            edit
                                        );
                                    }}
                                >
                                    <Button>Proceed</Button>
                                </Link>
                                <DialogClose asChild>
                                    <Button variant={"outline"}>Go back</Button>
                                </DialogClose>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            ) : (
                <Link
                    className={`w-full`}
                    href={blockUpdate.url}
                    target="_blank"
                    prefetch={false}
                    onClick={(e) => {
                        BlockClickHandler(account, e, block, edit);
                    }}
                    onAuxClick={(e) => {
                        BlockClickHandler(account, e, block, edit);
                    }}
                >
                    <button
                        style={style}
                        id="block"
                        className={cn(
                            "rounded-user bg-user-primary-color text-user-primary-text-color",
                            SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                            blockUpdate.style?.custom &&
                                BORDER_RADIUS_OPTIONS[
                                    blockUpdate.style.corner_radius
                                ]?.class,
                            blockUpdate.style?.custom &&
                                SHADOW_OPTIONS[blockUpdate.style.shadow]?.class,
                            "textsm w-full px-1 py-3 text-xl transition-all duration-150 hover:scale-105",
                            blockUpdate.style?.custom &&
                                FONT_SIZE_OPTIONS[blockUpdate.style.font_size]
                                    ?.class,
                            block.name.replaceAll(" ", "-"),
                            "block-" + block.id
                        )}
                        {...dataAttributes}
                    >
                        <RenderIconOrAsset
                            account_id={account.id}
                            iconName={blockUpdate.icon}
                            size={
                                (blockUpdate.style?.custom &&
                                    FONT_SIZE_OPTIONS[
                                        blockUpdate.style.font_size
                                    ]?.icon) ||
                                FONT_SIZE_OPTIONS[
                                    theme.profile.design.font_size
                                ]?.icon ||
                                26
                            }
                        />
                        <span
                            id="block-text"
                            className={`block w-full px-6 text-center font-bold`}
                        >
                            {blockUpdate.name}
                        </span>
                    </button>
                </Link>
            )}
        </AdminPopover>
    );
}
